import { useState, useEffect } from 'react';
import { useReactFlow, useNodeId } from '@xyflow/react';
import JSON5 from 'json5';
import i18n from '../../../../i18n/config';

interface InputField {
  id: string;
  title: string;
  type: string;
  value: string;
  placeholder?: string;
  required?: boolean;
  lines?: number;
}

const DynamicPromptContent = ({ data }: { data: any }) => {
  const { updateNodeData } = useReactFlow();
  const nodeId = useNodeId();
  const [template, setTemplate] = useState(data.template || '');
  const [inputFields, setInputFields] = useState<InputField[]>([]);
  const [outputPrompt, setOutputPrompt] = useState(data.text || '');
  const [advancedMode, setAdvancedMode] = useState(false);

  // 解析模板
  useEffect(() => {
    const parseTemplate = (template: string) => {
      const fields: InputField[] = [];
      const regex = /\{\{([\s\S]+?)\}\}/g;
      let match;

      while ((match = regex.exec(template)) !== null) {
        try {
          const jsonStr = '{' + match[1] + '}';
          const fieldProps = JSON5.parse(jsonStr);

          if (fieldProps.title && fieldProps.type) {
            fields.push({
              id: `field-${fields.length}`,
              title: fieldProps.title,
              type: fieldProps.type,
              value: '',
              placeholder: fieldProps.placeholder,
              required: fieldProps.required,
              lines: fieldProps.lines
            });
          }
        } catch (e) {
          console.error('解析错误:', e);
        }
      }

      return fields;
    };

    if (template) {
      const fields = parseTemplate(template);
      setInputFields(fields);
    }
  }, [template]);

  // 更新输出prompt
  useEffect(() => {
    if (!template) return;

    const replaceFields = (template: string, fields: InputField[]) => {
      let result = template;
      const matches: RegExpMatchArray[] = [];
      const regex = /\{\{([^}]+)\}\}/g;
      let match;

      // 首先收集所有匹配
      while ((match = regex.exec(template)) !== null) {
        matches.push(match);
      }

      // 然后按顺序替换
      fields.forEach((field, index) => {
        if (matches[index]) {
          result = result.replace(matches[index][0], field.value);
        }
      });

      return result;
    };

    const result = replaceFields(template, inputFields);
    setOutputPrompt(result);
  }, [template, inputFields]);

  // 更新节点数据
  useEffect(() => {
    const timer = setTimeout(() => {
      if (nodeId) {
        updateNodeData(nodeId, {
          template,
          inputFields,
          text: outputPrompt
        });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [template, inputFields, outputPrompt, nodeId, updateNodeData]);

  // 处理字段变化
  const handleFieldChange = (id: string, value: string) => {
    setInputFields(prev =>
      prev.map(field =>
        field.id === id ? { ...field, value } : field
      )
    );
  };

  return (
    <div className="flex flex-col gap-2 p-1">
      {/* 输入字段（始终显示，在高级模式开关上方） */}
      {inputFields.length > 0 ? (
        inputFields.map((field) => (
          <div key={field.id} className="flex flex-col gap-1">
            <label className="text-xs text-slate-400">{field.title}</label>
            {field.type === 'inputtext' && (
              field.lines && field.lines > 1 ? (
                <textarea
                  className="w-full bg-slate-900 border border-slate-700 rounded p-2 text-xs text-slate-200 focus:outline-none focus:border-blue-500 nodrag"
                  style={{ height: `${field.lines * 1.5}rem` }}
                  value={field.value}
                  onChange={(e) => handleFieldChange(field.id, e.target.value)}
                  placeholder={field.placeholder || `Enter ${field.title}...`}
                  onKeyDown={(e) => e.stopPropagation()}
                />
              ) : (
                <input
                  className="w-full bg-slate-900 border border-slate-700 rounded p-2 text-xs text-slate-200 focus:outline-none focus:border-blue-500 nodrag"
                  value={field.value}
                  onChange={(e) => handleFieldChange(field.id, e.target.value)}
                  placeholder={field.placeholder || `Enter ${field.title}...`}
                  onKeyDown={(e) => e.stopPropagation()}
                />
              )
            )}
          </div>
        ))
      ) : (
        <div className="text-xs text-slate-500">
          {i18n.t('canvas.dynamicPrompt.noInputFields')}
        </div>
      )}

      {/* 间距 */}
      <div className="h-2"></div>

      {/* 高级模式切换 */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-400">{i18n.t('canvas.dynamicPrompt.advancedMode')}</span>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={advancedMode}
            onChange={(e) => setAdvancedMode(e.target.checked)}
          />
          <div className="w-9 h-5 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-500"></div>
        </label>
      </div>

      {/* 高级模式内容 */}
      {advancedMode && (
        <>
          {/* Template */}
          <div className="mt-2">
            <label className="text-xs text-blue-400">{i18n.t('canvas.dynamicPrompt.template')}</label>
            <textarea
              className="w-full h-24 bg-slate-900 border border-slate-700 rounded p-2 text-xs text-slate-200 resize-none focus:outline-none focus:border-blue-500 nodrag"
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
              placeholder="Enter template with JSON5 format..."
              onKeyDown={(e) => e.stopPropagation()}
            />
          </div>

          {/* Output Prompt */}
          <div className="mt-2">
            <label className="text-xs text-blue-400">{i18n.t('canvas.dynamicPrompt.outputPrompt')}</label>
            <div className="w-full h-16 bg-slate-900 border border-slate-700 rounded p-2 text-xs text-slate-200 overflow-auto nodrag">
              {outputPrompt}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DynamicPromptContent;